<template>
  <div class="form-section">
    <h4 class="text-responsive-lg font-bold text-center mb-responsive text-ietextlight">
      Contact Information
    </h4>
    
    <div class="p-4 border rounded-md bg-white shadow-sm mb-6">
      <div class="space-y-responsive">
        <!-- Policy Number Field -->
        <FormField
          label="Policy Number"
          :field-id="policyNumberId"
          :error="getFieldError('policyNumber')"
          :initializing="initializing"
          is-primary
          required
          help-text="Your policy number can be found on your insurance card or policy documents."
          example="ABC123456"
          :userRole="customerType"
        >
          <template #rich-help-trigger>
            <div
              @mouseover="showPolicyNumberTooltip = true"
              @mouseleave="hidePolicyNumberTooltipWithDelay"
              class="relative flex items-center ml-1.5 cursor-help text-gray-400 hover:text-gray-600 transition-colors"
              aria-haspopup="true"
              :aria-expanded="showPolicyNumberTooltip.toString()"
            >
              <IconifyIcon
                icon="mdi:information-outline"
                style="height: 1em; width: 1em"
              />
              <transition name="fade">
                <div
                  v-if="showPolicyNumberTooltip"
                  role="tooltip"
                  class="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 px-4 py-2 bg-gray-800 text-white rounded-lg shadow-lg text-xs w-64 z-10"
                  style="min-width: 250px"
                >
                  If you do not know your policy number, use our
                  <router-link to="/policy-search" class="text-blue-400 hover:underline">
                    "Policy Search"
                  </router-link>
                  feature. <span class="block mt-1 text-gray-300 text-xs">This policy number is also on your insurance card or policy documents.</span>
                </div>
              </transition>
            </div>
          </template>

          <PolicyNumberInput 
            :id="policyNumberId"
            :model-value="policyNumber" 
            @update:model-value="emit('update:policyNumber', $event); touchedFields.policyNumber = true"
            @focus="touchedFields.policyNumber = true"
            :hasError="!!getFieldError('policyNumber')"
            :userRole="customerType"
            class="custom-styled"
          />
        </FormField>

        <div class="form-grid-2">
          <!-- First Name Field -->
          <FormField
            label="First Name"
            field-id="firstName"
            :error="getFieldError('firstName')"
            :initializing="initializing"
            is-primary
            required
            :userRole="customerType"
          >
            <div class="relative custom-styled">
              <input
                id="firstName"
                :value="firstName"
                @input="emit('update:firstName', $event.target.value); touchedFields.firstName = true"
                @focus="touchedFields.firstName = true"
                type="text"
                :class="[
                  'input-responsive',
                  { 'border-red-300 bg-red-50 focus:ring-red-500 focus:border-red-500': !!getFieldError('firstName') },
                  !getFieldError('firstName') ? (
                    customerType === 'customer' ? 'focus:ring-green-500 focus:border-green-500' : 'focus:ring-primary-500 focus:border-primary-500'
                  ) : ''
                ]"
                placeholder="Enter your first name"
              />
            </div>
          </FormField>
          
          <!-- Last Name Field -->
          <FormField
            label="Last Name"
            field-id="lastName"
            :error="getFieldError('lastName')"
            :initializing="initializing"
            is-primary
            required
            :userRole="customerType"
          >
            <div class="relative custom-styled">
              <input
                id="lastName"
                :value="lastName"
                @input="emit('update:lastName', $event.target.value); touchedFields.lastName = true"
                @focus="touchedFields.lastName = true"
                type="text"
                :class="[
                  'input-responsive',
                  { 'border-red-300 bg-red-50 focus:ring-red-500 focus:border-red-500': !!getFieldError('lastName') },
                  !getFieldError('lastName') ? (
                    customerType === 'customer' ? 'focus:ring-green-500 focus:border-green-500' : 'focus:ring-primary-500 focus:border-primary-500'
                  ) : ''
                ]"
                placeholder="Enter your last name"
              />
            </div>
          </FormField>
        </div>
        
        <!-- Phone Number Field -->
        <FormField
          label="Phone Number"
          field-id="phoneNumber"
          :error="getFieldError('phoneNumber')"
          :initializing="initializing"
          required
          help-text="We may need to contact you regarding your request."
          example="(555) 123-4567"
          :userRole="customerType"
        >
          <div class="relative custom-styled">
            <input
              id="phoneNumber"
              :value="phoneNumber"
              @input="emit('update:phoneNumber', $event.target.value); touchedFields.phoneNumber = true"
              @focus="touchedFields.phoneNumber = true"
              type="tel"
              :class="[
                'input-responsive',
                { 'border-red-300 bg-red-50 focus:ring-red-500 focus:border-red-500': !!getFieldError('phoneNumber') },
                !getFieldError('phoneNumber') ? (
                  customerType === 'customer' ? 'focus:ring-green-500 focus:border-green-500' : 'focus:ring-primary-500 focus:border-primary-500'
                ) : ''
              ]"
              placeholder="(XXX) XXX-XXXX"
            />
          </div>
        </FormField>
        
        <!-- Email Field -->
        <FormField
          label="Email"
          field-id="email"
          :error="getFieldError('email')"
          :initializing="initializing"
          required
          help-text="We'll send a confirmation to this email address."
          :userRole="customerType"
        >
          <div class="relative custom-styled">
            <input
              id="email"
              :value="email"
              @input="emit('update:email', $event.target.value); touchedFields.email = true"
              @focus="touchedFields.email = true"
              type="email"
              :class="[
                'input-responsive',
                { 'border-red-300 bg-red-50 focus:ring-red-500 focus:border-red-500': !!getFieldError('email') },
                !getFieldError('email') ? (
                  customerType === 'customer' ? 'focus:ring-green-500 focus:border-green-500' : 'focus:ring-primary-500 focus:border-primary-500'
                ) : ''
              ]"
              placeholder="Email for confirmation and communication"
            />
          </div>
        </FormField>
        
        <!-- Additional Fields Slot -->
        <slot name="additional-fields"></slot>
      </div>
    </div>
    
    <!-- Action Button -->
    <div class="flex justify-end mt-responsive">
      <button
        @click="handleNext"
        :class="customerType === 'lender' ? 'btn-lender' : 'btn-customer'"
        class="mobile-enhanced-button"
        :disabled="!props.isStepCurrentlyValid"
      >
        Next
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, watchEffect, computed, onUnmounted } from 'vue';
import { Icon as IconifyIcon } from '@iconify/vue';
import PolicyNumberInput from '@/components/common/PolicyNumberInput.vue';
import FormField from '@/components/common/FormField.vue';

// Props for component
const props = defineProps({
  firstName: {
    type: String,
    default: ''
  },
  lastName: {
    type: String,
    default: ''
  },
  policyNumber: {
    type: String,
    required: true
  },
  phoneNumber: {
    type: String,
    default: ''
  },
  email: {
    type: String,
    required: true
  },
  customerType: {
    type: String,
    default: 'customer'
  },
  errors: {
    type: Object,
    default: () => ({})
  },
  showErrors: {
    type: Boolean,
    default: undefined
  },
  initializing: {
    type: Boolean,
    default: false
  },
  isStepCurrentlyValid: {
    type: Boolean,
    default: true
  }
});

// Track which fields have been touched by the user
const touchedFields = ref({
  firstName: false,
  lastName: false,
  policyNumber: false,
  phoneNumber: false,
  email: false
});

// Generate unique ID for policy number field
const policyNumberId = `policy-number-${Math.random().toString(36).substring(2, 9)}`;

// Internal state for policy number tooltip
const showPolicyNumberTooltip = ref(false);
const policyNumberTooltipHideDelay = 800; // milliseconds
let policyNumberHideTimeoutId = null;

const hidePolicyNumberTooltipWithDelay = () => {
  clearTimeout(policyNumberHideTimeoutId);
  policyNumberHideTimeoutId = setTimeout(() => {
    showPolicyNumberTooltip.value = false;
  }, policyNumberTooltipHideDelay);
};

// Helper function to get field error only if the field has been touched
const getFieldError = (fieldName) => {
  // If showErrors is explicitly provided as a prop, respect it strictly
  if (props.showErrors !== undefined) {
    const shouldShow = props.showErrors === true;
    console.log(`getFieldError(${fieldName}): showErrors=${shouldShow}, error=${props.errors[fieldName] || 'none'}`);
    return shouldShow ? props.errors[fieldName] : '';
  }
  // Otherwise use our internal touchedFields tracking
  return touchedFields.value[fieldName] ? props.errors[fieldName] : '';
};

// Define emits
const emit = defineEmits([
  'update:firstName',
  'update:lastName',
  'update:policyNumber',
  'update:phoneNumber',
  'update:email',
  'next'
]);

// --- BEGIN LOGGING ---
watchEffect(() => {
  console.log('[SharedContactInfoStep] Props updated:', JSON.parse(JSON.stringify(props)));
  console.log('[SharedContactInfoStep] isStepCurrentlyValid:', props.isStepCurrentlyValid);
});

const buttonClasses = computed(() => {
  const classes = {
    'mobile-enhanced-button': true,
    'btn-customer': props.customerType === 'customer',
    'btn-lender': props.customerType === 'lender'
  };
  console.log('[SharedContactInfoStep] Button classes:', classes);
  console.log('[SharedContactInfoStep] Button disabled state:', !props.isStepCurrentlyValid);
  return classes;
});
// --- END LOGGING ---

// Helper function to handle next button click
const handleNext = () => {
  // --- BEGIN LOGGING ---
  console.log('[SharedContactInfoStep] handleNext called. isStepCurrentlyValid:', props.isStepCurrentlyValid);
  // --- END LOGGING ---

  // Set all fields as touched when trying to proceed
  Object.keys(touchedFields.value).forEach(field => {
    touchedFields.value[field] = true;
  });
  
  // Emit next event
  emit('next');
};

// Clear timeout on unmount
onUnmounted(() => {
  clearTimeout(policyNumberHideTimeoutId);
});
</script>

<style scoped>
.form-section {
  @apply p-4 md:p-6;
}

.form-grid-2 {
  @apply grid grid-cols-1 md:grid-cols-2 gap-4;
}

.form-field {
  @apply mb-4 md:mb-6;
}

.input-responsive {
  @apply w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none;
  @apply py-3 px-4 text-base sm:py-2 sm:px-3 sm:text-sm;
}

.space-y-responsive {
  @apply space-y-4 md:space-y-6;
}

.mb-responsive {
  @apply mb-4 md:mb-6;
}

.mt-responsive {
  @apply mt-4 md:mt-6;
}

.text-responsive-lg {
  @apply text-lg md:text-xl;
}
</style> 